import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CUSTOMER_TYPE, timeAgo } from "@/lib/format";
import { PageHeader, EmptyState, LoadingRows, Field, Segmented, TableShell, Th, trCls } from "@/components/drift/common";
import { CompanyMark } from "@/components/drift/CompanyMark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

export function NewCustomerDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState({ name: "", type: "privat", contact_name: "", email: "", phone: "", address: "", org_nr: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Kundenamn manglar"); return; }
    setBusy(true);
    try {
      const r = await api.post("/drift/customers", f);
      toast.success("Kunde oppretta");
      onOpenChange(false);
      setF({ name: "", type: "privat", contact_name: "", email: "", phone: "", address: "", org_nr: "" });
      onCreated && onCreated(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg" data-testid="new-customer-dialog">
        <DialogHeader><DialogTitle className="font-display">Ny kunde</DialogTitle><DialogDescription>Kunden får eit anlegg automatisk ved første oppdrag.</DialogDescription></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Namn" className="sm:col-span-2"><Input value={f.name} onChange={set("name")} data-testid="new-customer-name-input" /></Field>
          <Field label="Type"><Select value={f.type} onValueChange={set("type")}><SelectTrigger data-testid="new-customer-type-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(CUSTOMER_TYPE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></Field>
          <Field label="Org.nr"><Input value={f.org_nr} onChange={set("org_nr")} /></Field>
          <Field label="Kontaktperson"><Input value={f.contact_name} onChange={set("contact_name")} /></Field>
          <Field label="Telefon"><Input value={f.phone} onChange={set("phone")} /></Field>
          <Field label="E-post" className="sm:col-span-2"><Input value={f.email} onChange={set("email")} /></Field>
          <Field label="Adresse" className="sm:col-span-2"><Input value={f.address} onChange={set("address")} data-testid="new-customer-address-input" /></Field>
        </div>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button><Button onClick={submit} disabled={busy} className="bg-brand text-brand-foreground hover:bg-brand-dark" data-testid="new-customer-submit-button">{busy ? "Lagrar …" : "Opprett kunde"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TYPES = [["alle", "Alle"], ["industri", "Industri"], ["offentleg", "Offentleg"], ["naering", "Næring"], ["privat", "Privat"]];

export default function Customers() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("alle");
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/drift/customers?type=${type}${q ? `&q=${encodeURIComponent(q)}` : ""}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [q, type]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="customers-page">
      <PageHeader eyebrow="Register" title="Kundar og anlegg" subtitle="Kunde → anlegg → installasjonar → historikk. Kunnskapsbasen om anlegga vi har ansvar for."
        actions={<Button onClick={() => setOpen(true)} className="h-9 rounded-lg bg-brand text-brand-foreground hover:bg-brand-dark" data-testid="customers-new-button"><Plus className="mr-1.5 h-4 w-4" strokeWidth={2} /> Ny kunde</Button>} />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="drift-table-filters">
        <Segmented options={TYPES} value={type} onChange={setType} testid="customers-filter" />
        <div className="relative sm:w-80"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.75} aria-hidden /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk kunde, kontakt, adresse …" className="h-9 rounded-lg bg-card pl-9" data-testid="customers-search-input" aria-label="Søk i kundar" /></div>
      </div>
      {items === null ? <LoadingRows n={8} header /> : items.length === 0 ? <EmptyState icon={Building2} title="Ingen kundar" text="Opprett ein kunde, eller konverter ei henvending." /> : (
        <TableShell testid="drift-data-table">
            <thead className="border-b border-border/70">
              <tr><Th>Kunde</Th><Th className="hidden md:table-cell">Type</Th><Th className="hidden lg:table-cell">Kontakt</Th><Th align="right">Anlegg</Th><Th align="right">Oppdrag</Th><Th className="hidden sm:table-cell" align="right">Sist aktivitet</Th></tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {items.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/drift/kundar/${c.id}`)} onKeyDown={(e) => { if (e.key === "Enter") navigate(`/drift/kundar/${c.id}`); }} tabIndex={0} className={trCls} data-testid="customer-row">
                  <td className="px-4 py-3"><div className="flex items-center gap-3"><CompanyMark name={c.name} brand={c.brand} type={c.type} size="md" /><div className="min-w-0"><p className="truncate font-medium">{c.name}</p><p className="truncate text-[12.5px] text-muted-foreground">{c.address}</p></div></div></td>
                  <td className="hidden px-4 py-3 text-muted-foreground md:table-cell">{CUSTOMER_TYPE[c.type]}</td>
                  <td className="hidden px-4 py-3 text-muted-foreground lg:table-cell">{c.contact_name}{c.phone ? <span className="tabular ml-2 font-mono text-[12px]">{c.phone}</span> : null}</td>
                  <td className="px-4 py-3 text-right font-mono text-[13px] tabular">{c.location_count}</td>
                  <td className="px-4 py-3 text-right font-mono text-[13px] tabular">{c.order_count}</td>
                  <td className="hidden px-4 py-3 text-right text-[12.5px] text-muted-foreground sm:table-cell">{c.last_activity ? timeAgo(c.last_activity) : "–"}</td>
                </tr>
              ))}
            </tbody>
        </TableShell>
      )}
      <NewCustomerDialog open={open} onOpenChange={setOpen} onCreated={(c) => navigate(`/drift/kundar/${c.id}`)} />
    </div>
  );
}
